import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { 
  ChevronRight, 
  GitPullRequest, 
  Plus, 
  Calendar, 
  User, 
  MessageSquare, 
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  GitMerge,
  Filter,
  Edit,
  GitBranch,
  Users,
  GitFork,
  BarChart3
} from 'lucide-react';
import { getPullRequests, createPullRequest, getBranches, getPrompt } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import PromptTabs from '../components/PromptTabs';

const PromptPullRequests = ({ user }) => {
  const { promptId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [prompt, setPrompt] = useState(null);
  const [pullRequests, setPullRequests] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'open');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPR, setNewPR] = useState({
    title: '',
    description: '',
    sourceBranchId: searchParams.get('source') || '',
    targetBranchId: '',
    assignedTo: '',
    isDraft: false
  });

  useEffect(() => {
    loadData();
  }, [promptId, statusFilter]);

  const loadData = async () => {
    try {
      
      const [promptData, prData, branchesData] = await Promise.all([
        getPrompt(promptId),
        getPullRequests(promptId, user.id, statusFilter),
        getBranches(promptId, user.id)
      ]);
    
      
      setPrompt(promptData);
      setPullRequests(prData);
      setBranches(branchesData);
    } catch (error) {
      console.error('Failed to load data:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Show more specific error message
      if (error.response?.status === 404) {
        console.error('Prompt not found - this might be a database issue');
      } else if (error.response?.status === 403) {
        console.error('Access denied - this might be a permission issue');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePR = async () => {
    if (!newPR.title.trim() || !newPR.sourceBranchId || !newPR.targetBranchId) return;

    try {
      await createPullRequest({
        promptId,
        sourceBranchId: newPR.sourceBranchId,
        targetBranchId: newPR.targetBranchId,
        title: newPR.title,
        description: newPR.description,
        assignedTo: newPR.assignedTo || null,
        isDraft: newPR.isDraft,
        userId: user.id
      });

      setShowCreateModal(false);
      setNewPR({
        title: '',
        description: '',
        sourceBranchId: '',
        targetBranchId: '',
        assignedTo: '',
        isDraft: false
      });
      loadData();
    } catch (error) {
      console.error('Failed to create pull request:', error);
    }
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setSearchParams(status === 'all' ? {} : { status });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!prompt) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Prompt not found</h2>
        <Link to="/" className="text-primary-600 hover:text-primary-700 mt-2 inline-block">
          ← Back to Dashboard
        </Link>
      </div>
    );
  }

  const statusCounts = {
    open: pullRequests.filter(pr => pr.status === 'open').length,
    merged: pullRequests.filter(pr => pr.status === 'merged').length,
    closed: pullRequests.filter(pr => pr.status === 'closed').length,
    draft: pullRequests.filter(pr => pr.status === 'draft').length
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/" className="hover:text-primary-600">Dashboard</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/projects/${prompt.project_id}`} className="hover:text-primary-600">
          {prompt.project_name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <Link to={`/prompts/${prompt.id}`} className="hover:text-primary-600">
          {prompt.name}
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-gray-900">Pull Requests</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pull Requests</h1>
          <p className="text-gray-600 mt-2">
            Propose, review, and merge changes to {prompt.name}
          </p>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Pull Request
        </button>
      </div>

      {/* Collaboration Navigation Tabs */}
      <PromptTabs promptId={promptId} />

      {/* Filters */}
      <div className="flex items-center space-x-1">
        <Filter className="h-4 w-4 text-gray-500 mr-2" />
        {[
          { key: 'open', label: 'Open', count: statusCounts.open },
          { key: 'merged', label: 'Merged', count: statusCounts.merged },
          { key: 'closed', label: 'Closed', count: statusCounts.closed },
          { key: 'draft', label: 'Draft', count: statusCounts.draft },
          { key: 'all', label: 'All', count: pullRequests.length }
        ].map((filter) => (
          <button
            key={filter.key}
            onClick={() => handleStatusFilterChange(filter.key)}
            className={`px-3 py-1 text-sm rounded-md ${
              statusFilter === filter.key
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {filter.label} ({filter.count})
          </button>
        ))}
      </div>

      {/* Pull Requests List */}
      <div className="card">
        {pullRequests.length === 0 ? (
          <div className="text-center py-12">
            <GitPullRequest className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No pull requests found</h3>
            <p className="text-gray-600 mb-4">
              {statusFilter === 'open' 
                ? 'No open pull requests for this prompt'
                : `No ${statusFilter} pull requests for this prompt`
              }
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              Create your first pull request
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {pullRequests.map((pr) => (
              <PRCard key={pr.id} pr={pr} promptId={promptId} />
            ))}
          </div>
        )}
      </div>

      {/* Create PR Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Pull Request</h3>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="prTitle" className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  id="prTitle"
                  value={newPR.title}
                  onChange={(e) => setNewPR({ ...newPR, title: e.target.value })}
                  className="input"
                  placeholder="Brief description of your changes"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="sourceBranch" className="block text-sm font-medium text-gray-700 mb-1">
                    Compare (source branch)
                  </label>
                  <select
                    id="sourceBranch"
                    value={newPR.sourceBranchId}
                    onChange={(e) => setNewPR({ ...newPR, sourceBranchId: e.target.value })}
                    className="input"
                    required
                  >
                    <option value="">Select source branch</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name} {branch.is_default ? '(default)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="targetBranch" className="block text-sm font-medium text-gray-700 mb-1">
                    Base (target branch)
                  </label>
                  <select
                    id="targetBranch"
                    value={newPR.targetBranchId}
                    onChange={(e) => setNewPR({ ...newPR, targetBranchId: e.target.value })}
                    className="input"
                    required
                  >
                    <option value="">Select target branch</option>
                    {branches
                      .filter(branch => branch.id !== newPR.sourceBranchId)
                      .map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name} {branch.is_default ? '(default)' : ''}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label htmlFor="prDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="prDescription"
                  value={newPR.description}
                  onChange={(e) => setNewPR({ ...newPR, description: e.target.value })}
                  className="input"
                  rows={6}
                  placeholder="Describe your changes in detail..."
                />
              </div>
              
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newPR.isDraft}
                    onChange={(e) => setNewPR({ ...newPR, isDraft: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Create as draft</span>
                </label>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleCreatePR}
                className="btn-primary"
                disabled={!newPR.title.trim() || !newPR.sourceBranchId || !newPR.targetBranchId}
              >
                {newPR.isDraft ? 'Create Draft PR' : 'Create Pull Request'}
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewPR({
                    title: '',
                    description: '',
                    sourceBranchId: '',
                    targetBranchId: '',
                    assignedTo: '',
                    isDraft: false
                  });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PRCard = ({ pr, promptId }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <GitPullRequest className="h-4 w-4 text-celadon-600" />;
      case 'merged':
        return <GitMerge className="h-4 w-4 text-gray-600" />;
      case 'closed':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <GitPullRequest className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open':
        return 'badge-status-success';
      case 'merged':
        return 'badge-status-info';
      case 'closed':
        return 'badge-status-error';
      default:
        return 'badge-status-info';
    }
  };

  return (
    <div className="p-6 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            {getStatusIcon(pr.status)}
            
            <Link 
              to={`/prompts/${promptId}/pull-requests/${pr.id}`}
              className="text-lg font-semibold text-primary-600 hover:text-primary-700"
            >
              {pr.title}
            </Link>
            
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeClass(pr.status)}`}>
              {pr.status.charAt(0).toUpperCase() + pr.status.slice(1)}
            </span>
          </div>
          
          <div className="text-sm text-gray-600 mb-3">
            {pr.source_branch_name} → {pr.target_branch_name}
          </div>
          
          {pr.description && (
            <p className="text-gray-700 mb-3 line-clamp-2">{pr.description}</p>
          )}
          
          <div className="flex items-center space-x-6 text-sm text-gray-500">
            <div className="flex items-center">
              <User className="h-4 w-4 mr-1" />
              {pr.created_by_name || 'Unknown'}
            </div>
            
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              {formatDistanceToNow(new Date(pr.created_at), { addSuffix: true })}
            </div>
            
            {pr.comment_count > 0 && (
              <div className="flex items-center">
                <MessageSquare className="h-4 w-4 mr-1" />
                {pr.comment_count} comment{pr.comment_count !== 1 ? 's' : ''}
              </div>
            )}
            
            {pr.review_count > 0 && (
              <div className="flex items-center">
                <Eye className="h-4 w-4 mr-1" />
                {pr.review_count} review{pr.review_count !== 1 ? 's' : ''}
              </div>
            )}
            
            {pr.assigned_to_name && (
              <div className="flex items-center">
                <span className="text-gray-400">Assigned to:</span>
                <span className="ml-1 font-medium">{pr.assigned_to_name}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2 ml-4">
          <Link
            to={`/prompts/${promptId}/pull-requests/${pr.id}`}
            className="btn-secondary text-sm"
          >
            <Eye className="h-4 w-4 mr-1" />
            View
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PromptPullRequests; 